import React, { useState } from 'react';
import './ContactPage.css';
import { COMPANY } from '../data/ItineraryData';
import { supabase } from '../lib/supabase';
import logo from '../Gallery/logo.png';

const TRIP_TYPES = [
    'Honeymoon / Couples',
    'Family Holiday',
    'Group Tour',
    'Corporate / MICE',
    'Adventure Trip',
    'Visa Assistance Only',
    'Flight Booking',
    'Other',
];

const BUDGETS = [
    'Under ₹20,000 per person',
    '₹20,000 – ₹50,000 per person',
    '₹50,000 – ₹1,00,000 per person',
    'Above ₹1,00,000 per person',
    'Not decided yet',
];

const CONTACT_CARDS = [
    {
        icon: 'fa-solid fa-phone-volume',
        color: '#3e7e6a',
        label: 'Call Us',
        secondary: COMPANY.phone,
        primary: COMPANY.altPhone,
        action: `tel:${COMPANY.phone}`,
        actionLabel: 'Call Now',
    },
    {
        icon: 'fa-brands fa-whatsapp',
        color: '#25d366',
        label: 'WhatsApp',
        primary: COMPANY.altPhone,
        secondary: 'Chat with a travel expert',
        action: `https://wa.me/${COMPANY.phone.replace(/[^0-9]/g, '')}`,
        actionLabel: 'Open WhatsApp',
    },
    {
        icon: 'fa-solid fa-envelope-open-text',
        color: '#d4a437',
        label: 'Email Us',
        primary: COMPANY.email,
        secondary: 'Typically reply within 2 hours',
        action: `mailto:${COMPANY.email}`,
        actionLabel: 'Send Email',
    },
    {
        icon: 'fa-solid fa-location-dot',
        color: '#e74c3c',
        label: 'Office',
        primary: COMPANY.address,
        action: 'https://maps.google.com/?q=Ahmedabad,Gujarat,India',
        actionLabel: 'Get Directions',
    },
];

const HOURS = [
    { day: 'Monday – Friday', time: '9:00 AM – 8:00 PM' },
    { day: 'Saturday', time: '9:00 AM – 6:00 PM' },
    { day: 'Sunday', time: '10:00 AM – 4:00 PM (Inquiry Only)' },
];


/* ── Contact Form ─────────────────────────────────────── */
const ContactForm = () => {
    const [form, setForm] = useState({
        name: '', phone: '', email: '', destination: '',
        tripType: '', budget: '', travelDate: '', groupSize: '', message: '',
    });
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);
    const [submitError, setSubmitError] = useState('');

    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setSubmitError('');

        const notesParts = [
            form.tripType && `Trip: ${form.tripType}`,
            form.budget && `Budget: ${form.budget}`,
            form.message && `Notes: ${form.message}`,
        ].filter(Boolean);

        const { error } = await supabase.from('inquiries').insert([{
            name: form.name,
            phone: form.phone,
            email: form.email || null,
            country: form.destination,
            travel_date: form.travelDate ? `${form.travelDate}-01` : null,
            passengers: form.groupSize ? parseInt(form.groupSize, 10) : null,
            type: 'contact',
            status: 'new',
            notes: notesParts.join(' | ') || null,
        }]);

        setLoading(false);
        if (error) {
            console.error('Inquiry insert error:', error);
            setSubmitError(`Could not save inquiry: ${error.message}`);
        } else {
            setSubmitted(true);
        }
    };

    if (submitted) {
        return (
            <div className="ct-success">
                <div className="ct-success-icon"><i className="fa-solid fa-circle-check"></i></div>
                <h3>Thank You, {form.name.split(' ')[0]}!</h3>
                <p>Your inquiry has been received. One of our travel experts will call you within <strong>2 hours</strong> during office hours.</p>
                <div className="ct-success-alt">
                    <span>Need immediate help?</span>
                    <a href={`https://wa.me/${COMPANY.phone.replace(/[^0-9]/g, '')}`} className="ct-wa-pill" target="_blank" rel="noreferrer">
                        <i className="fa-brands fa-whatsapp"></i> WhatsApp Us Now
                    </a>
                </div>
                <button className="ct-reset-btn" onClick={() => { setSubmitted(false); setForm({ name: '', phone: '', email: '', destination: '', tripType: '', budget: '', travelDate: '', groupSize: '', message: '' }); }}>
                    Submit Another Inquiry
                </button>
            </div>
        );
    }

    return (
        <form className="ct-form" onSubmit={handleSubmit} noValidate>
            {/* Personal Details */}
            <div className="ct-form-section-label"><i className="fa-solid fa-user"></i> Your Details</div>
            <div className="ct-row">
                <div className="ct-field">
                    <label htmlFor="name">Full Name <span>*</span></label>
                    <input id="name" name="name" type="text" placeholder="e.g. Rajesh Shah" value={form.name} onChange={handleChange} required />
                </div>
                <div className="ct-field">
                    <label htmlFor="phone">Mobile Number <span>*</span></label>
                    <input id="phone" name="phone" type="tel" placeholder="+91 98765 43210" value={form.phone} onChange={handleChange} required />
                </div>
            </div>
            <div className="ct-field">
                <label htmlFor="email">Email Address</label>
                <input id="email" name="email" type="email" placeholder="you@example.com" value={form.email} onChange={handleChange} />
            </div>

            {/* Trip Details */}
            <div className="ct-form-section-label" style={{ marginTop: '24px' }}><i className="fa-solid fa-plane-departure"></i> Trip Details</div>
            <div className="ct-field">
                <label htmlFor="destination">Destination / Country Interested In <span>*</span></label>
                <input id="destination" name="destination" type="text" placeholder="e.g. Bali, Switzerland, Kerala" value={form.destination} onChange={handleChange} required />
            </div>
            <div className="ct-row">
                <div className="ct-field">
                    <label htmlFor="tripType">Trip Type</label>
                    <select id="tripType" name="tripType" value={form.tripType} onChange={handleChange}>
                        <option value="">— Select —</option>
                        {TRIP_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                </div>
                <div className="ct-field">
                    <label htmlFor="groupSize">Number of Travellers</label>
                    <input id="groupSize" name="groupSize" type="number" placeholder="e.g. 2" min="1" value={form.groupSize} onChange={handleChange} />
                </div>
            </div>
            <div className="ct-row">
                <div className="ct-field">
                    <label htmlFor="travelDate">Approx. Travel Date</label>
                    <input id="travelDate" name="travelDate" type="month" value={form.travelDate} onChange={handleChange} />
                </div>
                <div className="ct-field">
                    <label htmlFor="budget">Budget (per person)</label>
                    <select id="budget" name="budget" value={form.budget} onChange={handleChange}>
                        <option value="">— Select —</option>
                        {BUDGETS.map(b => <option key={b} value={b}>{b}</option>)}
                    </select>
                </div>
            </div>
            <div className="ct-field">
                <label htmlFor="message">Special Requests / Additional Info</label>
                <textarea id="message" name="message" rows="4" placeholder="Tell us anything — preferences, occasions, dietary needs, accessibility requirements..." value={form.message} onChange={handleChange} />
            </div>

            <button type="submit" className="ct-submit-btn" disabled={loading}>
                {loading
                    ? <><i className="fa-solid fa-spinner fa-spin"></i> Sending…</>
                    : <><i className="fa-solid fa-paper-plane"></i> Send My Inquiry</>
                }
            </button>
            {submitError && (
                <p style={{ color: '#dc2626', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 14px', marginTop: 10, fontSize: 13 }}>
                    <i className="fa-solid fa-triangle-exclamation" style={{ marginRight: 6 }}></i>{submitError}
                </p>
            )}
            <p className="ct-form-note"><i className="fa-solid fa-lock"></i> Your information is 100% private and never shared.</p>
        </form>
    );
};

/* ══════════════════════════════════════════
   CONTACT PAGE
══════════════════════════════════════════ */
const ContactPage = () => {

    return (
        <div className="ct-page">

            {/* ── HERO ──────────────────────────────────────────── */}
            <section className="ct-hero">
                <div className="ct-hero-bg" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1488085061387-422e29b40080?w=1600&auto=format&fit=crop&q=80')" }} />
                <div className="ct-hero-overlay" />
                <div className="ct-hero-content">
                    <div className="ct-hero-badge"><i className="fa-solid fa-headset"></i> We're Here for You</div>
                    <h1 className="ct-hero-title">Let's Plan Your<br /><span className="ct-hero-accent">Dream Holiday</span></h1>
                    <p className="ct-hero-sub">Reach out via call, WhatsApp, or email. Our travel experts are standing by to craft the perfect itinerary — just for you.</p>
                    <div className="ct-hero-pills">
                        <span><i className="fa-solid fa-clock"></i> Response within 2 hours</span>
                        <span><i className="fa-solid fa-indian-rupee-sign"></i> Free trip planning</span>
                        <span><i className="fa-solid fa-star"></i> 15,000+ happy travellers</span>
                    </div>
                </div>
                <div className="ct-hero-scroll"><i className="fa-solid fa-chevron-down"></i></div>
            </section>

            {/* ── CONTACT CARDS ──────────────────────────────────── */}
            <section className="ct-cards-section">
                <div className="ct-cards-inner">
                    {CONTACT_CARDS.map((card, i) => (
                        <a key={i} href={card.action} className="ct-card" target={card.action.startsWith('http') ? '_blank' : undefined} rel="noreferrer">
                            <div className="ct-card-icon" style={{ background: `${card.color}18`, color: card.color }}>
                                <i className={card.icon}></i>
                            </div>
                            <div className="ct-card-body">
                                <div className="ct-card-label">{card.label}</div>
                                <div className="ct-card-primary">{card.primary}</div>
                                <div className="ct-card-secondary">{card.secondary}</div>
                            </div>
                            <div className="ct-card-action" style={{ color: card.color }}>
                                {card.actionLabel} <i className="fa-solid fa-arrow-right"></i>
                            </div>
                        </a>
                    ))}
                </div>
            </section>

            {/* ── MAIN: FORM + SIDEBAR ───────────────────────────── */}
            <section className="ct-main-section">
                <div className="ct-main-inner">

                    {/* LEFT — Inquiry Form */}
                    <div className="ct-form-col">
                        <div className="ct-form-header">
                            <div className="ct-section-label"><i className="fa-solid fa-clipboard-list"></i> Send an Inquiry</div>
                            <h2 className="ct-section-title">Tell Us About<br />Your Dream Trip</h2>
                            <p className="ct-form-intro">Fill in the form below and one of our travel experts will call you back within 2 hours with a free, personalised itinerary quote.</p>
                        </div>
                        <ContactForm />
                    </div>

                    {/* RIGHT — Sidebar */}
                    <div className="ct-sidebar">

                        {/* Branding Card */}
                        <div className="ct-sidebar-card ct-brand-card">
                            <img src={logo} alt="Silver Sky Holidays" className="ct-brand-logo" />
                            <div className="ct-brand-name">Silver Sky Holidays</div>
                            <div className="ct-brand-tag">Relationship of Trust</div>
                            <div className="ct-brand-line"></div>
                            <p className="ct-brand-desc">Your trusted travel partner for holidays, visa &amp; flights across 30+ countries.</p>
                        </div>

                        {/* WhatsApp CTA */}
                        <div className="ct-wa-box">
                            <div className="ct-wa-icon"><i className="fa-brands fa-whatsapp"></i></div>
                            <div className="ct-wa-text">
                                <strong>Prefer WhatsApp?</strong>
                                <p>Chat with our team directly — share your ideas and get instant replies.</p>
                            </div>
                            <a href={`https://wa.me/${COMPANY.phone.replace(/[^0-9]/g, '')}?text=Hi%20Silver%20Sky%2C%20I%27d%20like%20to%20plan%20a%20trip!`} className="ct-wa-btn" target="_blank" rel="noreferrer">
                                <i className="fa-brands fa-whatsapp"></i> Chat on WhatsApp
                            </a>
                        </div>

                        {/* Office Hours */}
                        <div className="ct-sidebar-card">
                            <div className="ct-sidebar-card-title"><i className="fa-solid fa-clock"></i> Office Hours</div>
                            <div className="ct-hours-list">
                                {HOURS.map((h, i) => (
                                    <div key={i} className="ct-hour-row">
                                        <span className="ct-hour-day">{h.day}</span>
                                        <span className="ct-hour-time">{h.time}</span>
                                    </div>
                                ))}
                            </div>
                            <div className="ct-hours-note">
                                <i className="fa-solid fa-circle-info"></i>
                                For on-trip emergencies, our 24/7 travel desk is always reachable.
                            </div>
                        </div>

                        {/* Why Contact Us */}
                        <div className="ct-sidebar-card ct-why-card">
                            <div className="ct-sidebar-card-title"><i className="fa-solid fa-shield-halved"></i> Why Choose Us</div>
                            <ul className="ct-why-list">
                                {[
                                    { icon: 'fa-solid fa-earth-asia', text: 'Destinations across 30+ countries' },
                                    { icon: 'fa-solid fa-indian-rupee-sign', text: 'No hidden costs — ever' },
                                    { icon: 'fa-solid fa-sliders', text: '100% customised packages' },
                                    { icon: 'fa-solid fa-passport', text: 'Visa for 25+ countries' },
                                    { icon: 'fa-solid fa-headset', text: '24/7 on-trip support' },
                                    { icon: 'fa-solid fa-star', text: '15,000+ happy families served' },
                                    { icon: 'fa-solid fa-piggy-bank', text: 'Best price guarantee' },
                                ].map((item, i) => (
                                    <li key={i}><i className={item.icon}></i> {item.text}</li>
                                ))}
                            </ul>
                        </div>

                        {/* Stats strip */}
                        <div className="ct-cert-strip">
                            <div className="ct-cert-item">
                                <i className="fa-solid fa-earth-asia"></i>
                                <span>30+<br />Countries</span>
                            </div>
                            <div className="ct-cert-divider" />
                            <div className="ct-cert-item">
                                <i className="fa-solid fa-users"></i>
                                <span>15,000+<br />Clients</span>
                            </div>
                            <div className="ct-cert-divider" />
                            <div className="ct-cert-item">
                                <i className="fa-solid fa-headset"></i>
                                <span>24/7<br />Support</span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── MAP + OFFICE INFO ─────────────────────────────── */}
            <section className="ct-map-section">
                <div className="ct-map-inner">
                    <div className="ct-map-info">
                        <div className="ct-section-label"><i className="fa-solid fa-building"></i> Visit Us</div>
                        <h2 className="ct-section-title">Our Office</h2>
                        <div className="ct-office-details">
                            <div className="ct-office-row">
                                <i className="fa-solid fa-location-dot"></i>
                                <div>
                                    <strong>Silver Sky Holidays</strong>
                                    <p>B/702 Spectra , B/H TRP Mall Central Bopal Ahmedabad : 380058</p>
                                </div>
                            </div>
                            <div className="ct-office-row">
                                <i className="fa-solid fa-phone-volume"></i>
                                <div>
                                    <strong>Phone</strong>
                                    <p><a href={`tel:${COMPANY.altPhone}`}>{COMPANY.altPhone}</a></p>
                                    <p><a href={`tel:${COMPANY.phone}`}>{COMPANY.phone}</a></p>
                                </div>
                            </div>
                            <div className="ct-office-row">
                                <i className="fa-solid fa-envelope"></i>
                                <div>
                                    <strong>Email</strong>
                                    <p><a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a></p>
                                </div>
                            </div>
                            <div className="ct-office-row">
                                <i className="fa-solid fa-globe"></i>
                                <div>
                                    <strong>Website</strong>
                                    <p>{COMPANY.website}</p>
                                </div>
                            </div>
                        </div>
                        <div className="ct-social-row">
                            <a href="#!" className="ct-social-btn fb"><i className="fa-brands fa-facebook-f"></i></a>
                            <a href="#!" className="ct-social-btn ig"><i className="fa-brands fa-instagram"></i></a>
                            <a href="#!" className="ct-social-btn wa"><i className="fa-brands fa-whatsapp"></i></a>
                            <a href="#!" className="ct-social-btn yt"><i className="fa-brands fa-youtube"></i></a>
                        </div>
                    </div>
                    <div className="ct-map-embed">
                        <iframe
                            title="Silver Sky Holidays Location"
                            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d235013.54090702116!2d72.41492!3d23.02002!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x395e848aba5bd449%3A0x4fcedd11614f6516!2sAhmedabad%2C%20Gujarat!5e0!3m2!1sen!2sin!4v1708000000000!5m2!1sen!2sin"
                            allowFullScreen=""
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                        ></iframe>
                    </div>
                </div>
            </section>


            {/* ── BOTTOM CTA ────────────────────────────────────── */}
            <section className="ct-bottom-cta">
                <div className="ct-bottom-cta-inner">
                    <div className="ct-bottom-cta-text">
                        <h2>Ready to Start Planning?</h2>
                        <p>Call us right now — it takes 10 minutes to begin your dream holiday.</p>
                    </div>
                    <div className="ct-bottom-cta-btns">
                        <a href={`tel:${COMPANY.phone}`} className="ct-cta-btn ct-cta-primary">
                            <i className="fa-solid fa-phone"></i> {COMPANY.phone}
                        </a>
                        <a href={`https://wa.me/${COMPANY.phone.replace(/[^0-9]/g, '')}`} className="ct-cta-btn ct-cta-wa" target="_blank" rel="noreferrer">
                            <i className="fa-brands fa-whatsapp"></i> WhatsApp
                        </a>
                    </div>
                </div>
            </section>

        </div>
    );
};

export default ContactPage;
